<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useTranslation } from '@/utils/locales/messages';
import { useLanguageStore } from '@/shared/stores/languageStore';
import { useDialog } from '@/shared/composables/useDialog';
import { apiClient } from '@/shared/api/client';
import { useSessionStore } from '@/shared/stores/sessionStore';
// Change FYE 는 환경 무관하게 항상 파이썬 QR 경로 노출.
// 실제 활성 여부는 백엔드 pyEnabled() 응답(SG_NEW_DAEMON_URL/mTLS 세팅 유무)이 결정한다.
import SgPyLoginDialog from '@/features/startTask/preset_task/sg/components/change-financial-year-end/SgPyLoginDialog.vue';
import { sgPyScrapingApi } from '@/features/startTask/preset_task/sg/components/change-financial-year-end/composables/sgPyScrapingApi';
import ChangeFyeForm from './ChangeFyeForm.vue';

// Dialog
const { warning: showWarning } = useDialog();

function normalizeUen(raw: string): string {
  return String(raw || '').replace(/\s/g, '').trim();
}

const languageStore = useLanguageStore();
const sessionStore = useSessionStore();
const { createSyncT } = useTranslation();
const t = createSyncT(languageStore.currentLanguage);

// Props
interface Props {
  taskData?: any;
  taskId?: string;
  showBackButton?: boolean;
  readOnly?: boolean; // 읽기 전용 모드 (E-Filing 완료 상태)
}

const props = withDefaults(defineProps<Props>(), {
  taskData: null,
  taskId: '',
  showBackButton: true,
  readOnly: false
});

// Emits
const emit = defineEmits<{
  back: [];
  submit: [data: any];
  error: [error: any];
  'validation-success': [data: { eFilingData: any; entityUEN: string; filingAgentUEN: string; entityName: string }];
}>();

// Form ref
const formRef = ref<InstanceType<typeof ChangeFyeForm> | null>(null);

// 상태
const isSubmitting = ref(false);
const isValidating = ref(false);
const validationResult = ref<any>(null);

// Entity 정보
const entityUEN = computed(() => {
  return props.taskData?.formData?.RegistrationNo?.value ||
         props.taskData?.formData?.entityRegNo?.value ||
         props.taskData?.formData?.entity ||
         '';
});

const filingAgentUEN = computed(() => {
  const customerInfo = sessionStore.getCustomerInfo();
  return customerInfo?.regNo || entityUEN.value;
});

const entityName = computed(() => {
  return props.taskData?.formData?.CompanyName?.value ||
         props.taskData?.formData?.entityName?.value ||
         '';
});

// Back
const handleBack = () => emit('back');

/* ── DEV-LOCAL: 로컬 Python 스크래핑 경유 (운영은 기존 Tilko QR 그대로) ── */
const isDevLocal = true;
const pyEnabled = ref(false);
const showPyLogin = ref(false);
const pyBusy = ref(false);
const pyFilingAgentUEN = ref('');
const pyFye = ref<{ entityUEN: string; clientName: string; revisedDate: string; period: string }>({
  entityUEN: '',
  clientName: '',
  revisedDate: '',
  period: '',
});

onMounted(async () => {
  if (isDevLocal) {
    pyEnabled.value = await sgPyScrapingApi.enabled();
  }
});

/** GUID(내부 ENTITY_CD)인지 판별 — bizfile UEN 자리에 들어가면 CORELIB-DB-006 유발. */
function looksLikeGuid(v: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(v || '').trim());
}

function openPyLogin(formData: any, eFilingData: any) {
  // 실제 bizfile UEN 은 eFilingData.EntityUEN(기존 Tilko 경로와 동일). formData.entity 는 내부 GUID 라 금지.
  const entityU = normalizeUen(eFilingData?.EntityUEN || eFilingData?.entityUEN || entityUEN.value);
  const saveFye = eFilingData?.SaveFYEList?.[0] || {};
  const revised = saveFye.revisedFinancialYearEndDate || formData?.revisedFinancialYearEndDate || '';
  const period = saveFye.financialYearEndPeriod || '';
  if (!entityU || looksLikeGuid(entityU)) {
    void showWarning(
      t('startTask.Validation Error') || 'Validation Error',
      'A valid entity UEN (e.g. 202XXXXXXG) is required — got an internal code. Check the entity registration number.',
    );
    return;
  }
  if (!revised) {
    void showWarning(
      t('startTask.Validation Error') || 'Validation Error',
      'Revised financial year end date is required.',
    );
    return;
  }
  pyFye.value = { entityUEN: entityU, clientName: entityName.value || '', revisedDate: revised, period };
  pyFilingAgentUEN.value = normalizeUen(
    eFilingData?.FilingAgentUEN || eFilingData?.filingAgentUEN || filingAgentUEN.value,
  );
  showPyLogin.value = true;
}

async function onPyAuthenticated(payload: {
  sessionId: string;
  mstIdx: number | null;
  filingAgentUEN: string;
}) {
  showPyLogin.value = false;
  pyBusy.value = true;
  try {
    const r = await sgPyScrapingApi.changeFye({
      taskId: props.taskId,
      sessionId: payload.sessionId,
      mstIdx: payload.mstIdx,
      entityUEN: pyFye.value.entityUEN,
      clientName: pyFye.value.clientName,
      revisedFinancialYearEndDate: pyFye.value.revisedDate,
      financialYearEndPeriod: pyFye.value.period,
      changeReasons: [],
      otherReason: '',
    });
    if (r.success) {
      // 라이브 제출 성공과 동일하게 처리(부모 handleEFilingComplete → 태스크 완료 반영)
      emit('submit', { eFilingData: pyFye.value, apiResponse: r });
    } else {
      await showWarning(
        t('startTask.E-Filing Error') || 'E-Filing Error',
        r.error || t('startTask.E-Filing failed') || 'E-Filing failed. Please try again.',
      );
    }
  } catch (e: any) {
    await showWarning(
      t('startTask.E-Filing Error') || 'E-Filing Error',
      e?.message || t('startTask.E-Filing failed') || 'E-Filing failed. Please try again.',
    );
  } finally {
    pyBusy.value = false;
  }
}

// 최종 전송용 데이터 (eFilingDataForApi)
const finalEFilingData = ref<any>(null);

// Submit - E-Filing 데이터 생성 후 검증 및 저장
const handleSubmit = async () => {
  console.log('📋 [ChangeFyeEFiling] Submit 클릭');

  if (isSubmitting.value) return;

  try {
    isSubmitting.value = true;

    if (!formRef.value) {
      console.error('❌ [ChangeFyeEFiling] Form ref가 없습니다');
      return;
    }

    if (!props.taskId) {
      console.error('❌ [ChangeFyeEFiling] taskId가 없습니다');
      await showWarning(
        t('startTask.Error') || 'Error',
        t('startTask.Task ID is required') || 'Task ID is required.'
      );
      return;
    }

    // ChangeFyeForm의 getEFilingData 호출 (ControllerAppointmentForm 패턴)
    const updatedEFilingData = await formRef.value.getEFilingData?.();
    console.log('📋 [ChangeFyeEFiling] Updated E-Filing 데이터:', updatedEFilingData);

    // ChangeFyeForm의 getFormData 호출 (formFields 업데이트용)
    const currentFormData = formRef.value.getFormData?.();
    console.log('📋 [ChangeFyeEFiling] 현재 화면 값:', currentFormData);

    if (!currentFormData || !updatedEFilingData) {
      console.error('❌ [ChangeFyeEFiling] Form data 또는 E-Filing data를 가져올 수 없습니다');
      return;
    }

    // DEV-LOCAL: 기존 Tilko QR 대신 로컬 Python 로그인(QR/비밀번호) → change-fye 로 진행
    if (isDevLocal && pyEnabled.value) {
      openPyLogin(currentFormData, updatedEFilingData);
      return;
    }

    // formFields 준비
    const formFields = {
      entity: currentFormData.entity || '',
      revisedFinancialYearEndDate: currentFormData.revisedFinancialYearEndDate || '',
      financialYearPeriod: currentFormData.financialYearPeriod || ''
    };

    console.log('📋 [ChangeFyeEFiling] 업데이트할 formFields:', formFields);
    console.log('📋 [ChangeFyeEFiling] 업데이트할 eFilingDataForApi:', updatedEFilingData);

    // 바로 검증 및 저장 실행
    await handleValidateAndSubmit(formFields, updatedEFilingData);
  } finally {
    isSubmitting.value = false;
  }
};

// formFields 및 eFilingDataForApi 저장 후 검증 및 QR 모달 열기
const handleValidateAndSubmit = async (formFields: any, eFilingDataForApi: any) => {
  console.log('📋 [ChangeFyeEFiling] Save & Validate 호출');

  if (isValidating.value) return;

  try {
    isValidating.value = true;

    if (!props.taskId) {
      console.error('❌ [ChangeFyeEFiling] taskId가 없습니다');
      validationResult.value = {
        success: false,
        error: 'Task ID is required'
      };
      return;
    }

    // 1. formData.formFields 및 eFilingDataForApi 저장 (update-form-data API 호출)
    console.log('📤 [ChangeFyeEFiling] formFields 및 eFilingDataForApi 저장 중...');
    const saveResponse = await apiClient.put(
      `/startTask/change-fye/update-form-data/${props.taskId}`,
      {
        formData: {
          formFields
        },
        eFilingDataForApi
      }
    );
    console.log('✅ [ChangeFyeEFiling] formFields 및 eFilingDataForApi 저장 완료:', saveResponse);

    // 2. /validate/:taskId API 호출
    console.log('📤 [ChangeFyeEFiling] Validate 호출 중...');
    const response = await apiClient.get(`/startTask/change-fye/validate/${props.taskId}`);
    console.log('✅ [ChangeFyeEFiling] Validate 응답:', response);

    validationResult.value = {
      success: true,
      data: response?.data || response
    };

    // 검증 성공 시 최종 전송용 데이터 저장
    if (validationResult.value?.data?.isValid) {
      finalEFilingData.value = eFilingDataForApi;
      console.log('📋 [ChangeFyeEFiling] 최종 API용 데이터:', finalEFilingData.value);

      // 검증 성공 시 부모 컴포넌트(EFilingPage)로 데이터 전달하여 Singpass 모달 호출
      emit('validation-success', {
        eFilingData: finalEFilingData.value,
        entityUEN: eFilingDataForApi?.EntityUEN || entityUEN.value,
        filingAgentUEN: eFilingDataForApi?.FilingAgentUEN || filingAgentUEN.value,
        entityName: entityName.value
      });
    } else {
      // 검증 실패 시 알림창 표시
      await showWarning(
        t('startTask.Validation Error') || 'Validation Error',
        t('startTask.Please check the required fields and enter the values') || 'Please check the required fields and enter the values.'
      );
    }
  } catch (error: any) {
    console.error('❌ [ChangeFyeEFiling] Save & Validate 실패:', error);
    validationResult.value = {
      success: false,
      error: error.message || 'Save or Validation failed'
    };
    await showWarning(
      t('startTask.Error') || 'Error',
      error.message || t('startTask.Failed to save or validate') || 'Failed to save or validate.'
    );
    emit('error', error);
  } finally {
    isValidating.value = false;
  }
};

defineExpose({
  formRef
});
</script>

<template>
  <v-card rounded="md" class="change-fye-efiling ma-4">
    <v-card-text>
      <ChangeFyeForm
        ref="formRef"
        :initial-data="taskData"
        :read-only="readOnly"
        :show-task-flow="false"
        :e-filing-mode="true"
      />
    </v-card-text>

    <v-divider v-if="!readOnly" />
    <v-card-actions v-if="!readOnly" class="justify-end pa-4 pr-8">
      <v-btn
        v-if="showBackButton"
        variant="outlined"
        color="primary"
        :disabled="isSubmitting || isValidating"
        @click="handleBack"
      >
        {{ t('startTask.Back') || 'Back' }}
      </v-btn>
      <v-btn
        color="primary"
        variant="flat"
        :loading="isSubmitting || isValidating || pyBusy"
        :disabled="isSubmitting || isValidating || pyBusy"
        @click="handleSubmit"
      >
        {{ t('startTask.Submit') || 'Submit' }}
      </v-btn>
    </v-card-actions>

    <SgPyLoginDialog
      v-model="showPyLogin"
      :task-id="taskId"
      :filing-agent-u-e-n="pyFilingAgentUEN"
      @authenticated="onPyAuthenticated"
    />

    <v-divider v-if="readOnly" />
    <v-card-actions v-if="readOnly" class="justify-center pa-4">
      <v-alert type="success" variant="tonal" density="compact" class="mb-0">
        {{ t('startTask.E-Filing has been completed') || 'E-Filing has been completed' }}
      </v-alert>
    </v-card-actions>
  </v-card>
</template>

<style scoped>
.change-fye-efiling {
  max-width: 100%;
  box-sizing: border-box;
}
</style>

